import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { bindDeanSession, resetDeanSession } from "./deanChatSession";

const FETCH_TIMEOUT_MS = 30_000;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    try {
      const parsed = JSON.parse(text);
      const json = parsed && typeof parsed === "object" ? parsed : {};
      const base = json.message || json.error || text;
      const ref = json.ref ? ` (Reference: ${json.ref})` : "";
      const error = new Error(`${res.status}: ${base}${ref}`) as Error & {
        code?: string;
        status?: number;
      };
      error.code = typeof json.code === "string" ? json.code : undefined;
      error.status = res.status;
      throw error;
    } catch (e) {
      if (e instanceof SyntaxError) {
        throw new Error(`${res.status}: ${text}`, { cause: e });
      }
      throw e;
    }
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
      signal: controller.signal,
    });
    await throwIfResNotOk(res);
    if (method.toUpperCase() === "POST" && (
      url === "/api/auth/logout" || url.startsWith("/api/admin/impersonate")
    )) resetDeanSession(queryClient);
    return res;
  } catch (err: any) {
    if (err.name === "AbortError") {
      throw new Error("Request timed out after 30 seconds", { cause: err });
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export function getQueryFn<T>(options: {
  on401: UnauthorizedBehavior;
}): QueryFunction<T> {
  const { on401: unauthorizedBehavior } = options;
  return async ({ queryKey }) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(queryKey.join("/") as string, {
        credentials: "include",
        signal: controller.signal,
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      const json = await res.json();
      // Transparently unwrap paginated responses { data, total, limit, offset }
      // so existing consumers that expect a plain array continue to work.
      if (
        json !== null &&
        typeof json === "object" &&
        !Array.isArray(json) &&
        Array.isArray(json.data) &&
        typeof json.total === "number" &&
        typeof json.limit === "number" &&
        typeof json.offset === "number"
      ) {
        return json.data as unknown as T;
      }
      return json;
    } catch (err: any) {
      if (err.name === "AbortError") {
        throw new Error("Request timed out after 30 seconds", { cause: err });
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  };
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
    mutations: {
      retry: false,
    },
  },
});

bindDeanSession(queryClient);
